import React, { useEffect, useMemo, useState } from "react";
import AnalysisFieldCard from "./analysis/AnalysisFieldCard";
import AnalysisSection from "./analysis/AnalysisSection";
import DesignSolutionCard from "./analysis/DesignSolutionCard";
import { ExecutiveSummaryNarrative } from "./analysis/DesignSolutionContent";
import { STI_SECTIONS, fieldLabel } from "./analysis/analysisFieldMeta";
import { GAP_AREA_LABELS_ES, GAP_AREA_LABELS_PT, SCOPE_FIELD_LABELS_ES, SCOPE_FIELD_LABELS_PT } from "./scopeAnalysisLabels";
import {
  analysisLanguage,
  collectKeyGaps,
  collectWarningFields,
  getNestedValue,
  isFieldResult,
  qualitySummaryText,
} from "./analysis/analysisUtils";
import { formatGapListItem } from "./analysis/analysisDisplayUtils";
import { assembleScopeAnalysis, buildScopeSolutions } from "./assembleScopeAnalysis";
import { isLanguageRestatement } from "./scopeAnalysisDisplay";
import { fieldIsOnScopeCard } from "./scopeAnalysisFields";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "design", label: "Design" },
  { id: "sti", label: "Support / Training / Implementation" },
  { id: "commercial", label: "Commercial / Administrative" },
  { id: "gaps", label: "Gaps & Warnings" },
];

const TABS_ES = [
  { id: "overview", label: "Resumen" },
  { id: "design", label: "Diseño" },
  { id: "sti", label: "Soporte / Entrenamiento / Implementación" },
  { id: "commercial", label: "Comercial / Administrativo" },
  { id: "gaps", label: "Brechas y advertencias" },
];

const TABS_PT = [
  { id: "overview", label: "Visão geral" },
  { id: "design", label: "Design" },
  { id: "sti", label: "Suporte / Treinamento / Implementação" },
  { id: "commercial", label: "Comercial / Administrativo" },
  { id: "gaps", label: "Lacunas e avisos" },
];

const STI_LABELS_ES = {
  support: "Soporte",
  training: "Entrenamiento",
  implementation: "Implementación",
};

const STI_LABELS_PT = {
  support: "Suporte",
  training: "Treinamento",
  implementation: "Implementação",
};

function copy(language, en, es, pt) {
  if (language === "es") return es;
  if (language === "pt") return pt;
  return en;
}

function scopeFieldLabel(fieldId, language) {
  if (language === "es" && SCOPE_FIELD_LABELS_ES[fieldId]) {
    return SCOPE_FIELD_LABELS_ES[fieldId];
  }
  if (language === "pt" && SCOPE_FIELD_LABELS_PT[fieldId]) {
    return SCOPE_FIELD_LABELS_PT[fieldId];
  }
  return fieldLabel(fieldId, language);
}

function ScopeFieldCard({ fieldId, language, ...rest }) {
  return (
    <AnalysisFieldCard
      fieldId={fieldId}
      language={language}
      labelOverride={scopeFieldLabel(fieldId, language)}
      {...rest}
    />
  );
}

function SummaryCard({ title, children, className = "" }) {
  return (
    <div
      className={`rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900/80 ${className}`}
    >
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{title}</h3>
      <div className="text-sm leading-relaxed text-slate-700 dark:text-slate-200">{children}</div>
    </div>
  );
}

function OverviewTab({ analysis, language, onEvidenceClick }) {
  const exec = analysis?.general?.executive_summary;
  const clarification = analysis?.general?.customer_clarification_summary;
  const techs = analysis?.general?.technologies_involved;
  const areaLabels = language === "es" ? GAP_AREA_LABELS_ES : language === "pt" ? GAP_AREA_LABELS_PT : {};
  const gaps = collectKeyGaps(analysis, language, 8).map((gap) => ({
    ...gap,
    label: areaLabels[gap.label] || gap.label,
    ...formatGapListItem(gap.text, language),
  }));
  const quality = qualitySummaryText(analysis?.quality_summary, language);
  const clarificationParagraphs =
    clarification?.status === "found" && clarification.value
      ? Array.isArray(clarification.value)
        ? clarification.value
        : [clarification.value]
      : [];
  const techList = techs?.status === "found" && Array.isArray(techs.value) ? techs.value : [];

  return (
    <div className="space-y-3" data-testid="analysis-overview">
      <div className="grid items-stretch gap-3 lg:grid-cols-2">
        <SummaryCard
          className="h-full"
          title={copy(language, "Executive Summary", "Resumen ejecutivo", "Resumo executivo")}
        >
          <div className="max-w-[70ch]">
            <ExecutiveSummaryNarrative exec={exec} language={language} onEvidenceClick={onEvidenceClick} />
          </div>
        </SummaryCard>

        <SummaryCard
          className="h-full"
          title={copy(language, "Customer clarification summary", "Aclaración del cliente", "Esclarecimento do cliente")}
        >
          {clarificationParagraphs.length ? (
            <div className="max-w-[70ch] space-y-3">
              {clarificationParagraphs.map((paragraph, index) => (
                <p key={index} className="text-[15px] leading-relaxed text-slate-700 dark:text-slate-200">
                  {String(paragraph)}
                </p>
              ))}
            </div>
          ) : (
            <p className="text-slate-500">
              {copy(
                language,
                "No customer clarification summary available.",
                "No hay aclaración del cliente disponible.",
                "Nenhum esclarecimento do cliente disponível."
              )}
            </p>
          )}
        </SummaryCard>
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 dark:border-slate-700 dark:bg-slate-900/80">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {copy(language, "Technologies in Scope", "Tecnologías en alcance", "Tecnologias no escopo")}
        </span>
        {techList.length ? (
          <div className="flex flex-wrap gap-2">
            {techList.map((tech) => (
              <span
                key={tech}
                className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-sm font-medium text-slate-800 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
              >
                {tech}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-sm text-slate-500">
            {copy(language, "No technologies identified.", "No se identificaron tecnologías.", "Nenhuma tecnologia identificada.")}
          </span>
        )}
      </div>

      <SummaryCard title={copy(language, "Key Gaps / Missing Information", "Brechas / información faltante", "Lacunas / informações ausentes")}>
        {gaps.length ? (
          <ul className="grid gap-x-8 gap-y-1 sm:grid-cols-2">
            {gaps.map((gap, index) => (
              <li key={`${gap.fieldId}-${index}`} className="border-b border-slate-100 py-2.5 dark:border-slate-800">
                <p className="text-sm text-slate-800 dark:text-slate-100">
                  <span className="font-medium">{gap.label}</span>
                  <span className="text-slate-400"> · </span>
                  {gap.title}
                </p>
                {gap.detail ? (
                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{gap.detail}</p>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-slate-500">
            {copy(language, "No notable gaps detected.", "No se detectaron brechas relevantes.", "Nenhuma lacuna relevante detectada.")}
          </p>
        )}
      </SummaryCard>

      {quality ? (
        <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-300">
          <span className="font-medium text-slate-700 dark:text-slate-200">
            {copy(language, "Quality", "Calidad", "Qualidade")}
          </span>
          <span className="text-slate-400"> · </span>
          {quality}
        </p>
      ) : null}
    </div>
  );
}

function solutionFieldEntries(analysis, solution) {
  const wanted = new Set(solution?.card_ids || []);
  const entries = [];
  const seen = new Set();
  (analysis?.cards || []).forEach((card) => {
    if (wanted.size) {
      if (!wanted.has(card.card_id)) return;
    } else if (card.technology_id !== solution?.technology_id) {
      return;
    }
    const packs = [card.fields || {}, card.administrative_access?.fields || {}];
    packs.forEach((fields) => {
      Object.entries(fields).forEach(([fieldId, result]) => {
        const key = `${card.card_id}:${fieldId}`;
        if (seen.has(key) || !result || result.status === "not_found" || result.status === "not_applicable") return;
        if (!fieldIsOnScopeCard(card, fieldId)) return;
        seen.add(key);
        entries.push({ key, fieldId, result });
      });
    });
  });
  return entries;
}

function solutionSummary(analysis, solution, language = "en") {
  const entries = solutionFieldEntries(analysis, solution);
  const found = entries.filter(({ result }) => result.status === "found").length;
  if (!found) {
    return {
      chips: [copy(language, "No summary available", "Sin resumen", "Sem resumo")],
      muted: true,
    };
  }
  return {
    chips: [copy(language, `Found: ${found}`, `Encontrados: ${found}`, `Encontrados: ${found}`)],
    muted: false,
  };
}

function ScopeConflicts({ cards, solution, language }) {
  const wanted = new Set(solution?.card_ids || []);
  const relevant = (cards || [])
    .map((card) => {
      if (!(wanted.has(card.card_id) || card.technology_id === solution?.technology_id)) return null;
      const items = (card.analysis_conflicts || []).filter(
        (item) =>
          fieldIsOnScopeCard(card, item.field) &&
          !isLanguageRestatement(item.approved_value, item.analysis_detected_value)
      );
      if (!items.length) return null;
      return { card, items };
    })
    .filter(Boolean);
  if (!relevant.length) return null;
  return (
    <div className="mb-4 space-y-2">
      {relevant.map(({ card, items }) => (
        <div
          key={card.card_id}
          className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
          data-testid={`scope-analysis-conflicts-${card.technology_id || card.card_id}`}
        >
          <p className="font-medium">
            {copy(
              language,
              "Approved Design kept; Analysis found possible conflicts",
              "Se mantiene el diseño aprobado; el análisis encontró posibles conflictos",
              "O Design aprovado foi mantido; a análise encontrou possíveis conflitos"
            )}
          </p>
          <ul className="mt-1 list-disc pl-5" data-testid="scope-analysis-conflict-list">
            {items.map((item, index) => (
              <li key={`${item.field}-${index}`}>{scopeFieldLabel(item.field, language)}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function DesignTab({ analysis, language, onEvidenceClick }) {
  const solutions = useMemo(
    () =>
      analysis?.scope_solutions?.length
        ? analysis.scope_solutions
        : buildScopeSolutions(analysis?.cards || [], language),
    [analysis, language]
  );
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (!solutions.length) {
      setSelectedId(null);
      return;
    }
    if (!solutions.some((solution) => solution.id === selectedId)) {
      setSelectedId(solutions[0].id);
    }
  }, [solutions, selectedId]);

  const selected = solutions.find((solution) => solution.id === selectedId) || solutions[0];
  const selectedFields = selected ? solutionFieldEntries(analysis, selected) : [];
  const selectedSummary = selected ? solutionSummary(analysis, selected, language) : { chips: [], muted: false };

  return (
    <div className="space-y-4" data-testid="analysis-design">
      {solutions.length ? (
        <>
          <div className="flex flex-wrap gap-2" data-testid="design-solution-cards">
            {solutions.map((solution) => (
              <DesignSolutionCard
                key={solution.id}
                solution={solution}
                summary={solutionSummary(analysis, solution, language)}
                selected={solution.id === selected?.id}
                onSelect={setSelectedId}
              />
            ))}
          </div>

          {selected ? (
            <div className="space-y-4">
              <ScopeConflicts cards={analysis?.cards} solution={selected} language={language} />
              {selectedFields.length ? (
                <div className="grid items-start gap-3 sm:grid-cols-2" data-testid={`scope-solution-fields-${selected.technology_id}`}>
                  {selectedFields.map(({ key, fieldId, result }) => (
                    <ScopeFieldCard
                      key={key}
                      fieldId={fieldId}
                      result={result}
                      language={language}
                      muted={selectedSummary.muted}
                      onEvidenceClick={onEvidenceClick}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">
                  {copy(
                    language,
                    "No requirement fields for this solution.",
                    "No hay campos de requisitos para esta solución.",
                    "Não há campos de requisitos para esta solução."
                  )}
                </p>
              )}
            </div>
          ) : null}
        </>
      ) : (
        <p className="text-sm text-slate-500">
          {copy(
            language,
            "No in-scope design solutions to display.",
            "No hay soluciones de diseño en el alcance para mostrar.",
            "Não há soluções de design no escopo para exibir."
          )}
        </p>
      )}
    </div>
  );
}

function StiTab({ analysis, language, onEvidenceClick }) {
  return (
    <div className="space-y-6" data-testid="analysis-sti">
      {STI_SECTIONS.map((section) => (
        <AnalysisSection
          key={section.id}
          title={
            language === "es"
              ? STI_LABELS_ES[section.id] || section.label
              : language === "pt"
                ? STI_LABELS_PT[section.id] || section.label
                : section.label
          }
          node={getNestedValue(analysis, section.path)}
          pathPrefix={`sti.${section.id}`}
          language={language}
          showOutOfScope={false}
          onEvidenceClick={onEvidenceClick}
          getFieldLabel={(fieldId) => scopeFieldLabel(fieldId, language)}
        />
      ))}
    </div>
  );
}

function CommercialTab({ analysis, language, onEvidenceClick }) {
  const commercialFields = Object.entries(analysis?.commercial_administrative || {}).filter(
    ([key]) => !key.endsWith("_gaps")
  );

  return (
    <div className="grid items-start gap-3 sm:grid-cols-2" data-testid="analysis-commercial">
      {commercialFields.map(([key, result]) =>
        isFieldResult(result) && result.status !== "not_found" && result.status !== "not_applicable" ? (
          <ScopeFieldCard
            key={key}
            fieldId={`commercial_admin.${key}`}
            result={result}
            language={language}
            onEvidenceClick={onEvidenceClick}
          />
        ) : null
      )}
    </div>
  );
}

function GapsTab({ analysis, language, onEvidenceClick }) {
  const gapFields = [
    { id: "design.design_gaps", result: analysis?.design?.design_gaps },
    { id: "sti.sti_gaps", result: analysis?.support_training_implementation?.sti_gaps },
    { id: "commercial.commercial_admin_gaps", result: analysis?.commercial_administrative?.commercial_admin_gaps },
  ];
  const warningFields = collectWarningFields(analysis).filter(
    (item) => !String(item.fieldId || "").startsWith("cards.")
  );
  const globalWarnings = (analysis?.warnings || []).filter(Boolean);

  return (
    <div className="space-y-6" data-testid="analysis-gaps">
      {gapFields.map(({ id, result }) =>
        result && isFieldResult(result) && result.status !== "not_applicable" ? (
          <ScopeFieldCard key={id} fieldId={id} result={result} language={language} onEvidenceClick={onEvidenceClick} />
        ) : null
      )}

      {warningFields.length > 0 && (
        <section>
          <h3 className="mb-3 text-base font-semibold">
            {copy(language, "Fields needing attention", "Campos que requieren atención", "Campos que exigem atenção")}
          </h3>
          <div className="grid items-start gap-3 sm:grid-cols-2">
            {warningFields.map(({ fieldId, result }) => (
              <ScopeFieldCard
                key={fieldId}
                fieldId={fieldId}
                result={result}
                language={language}
                onEvidenceClick={onEvidenceClick}
              />
            ))}
          </div>
        </section>
      )}

      {globalWarnings.length > 0 && (
        <section>
          <h3 className="mb-2 text-base font-semibold">
            {copy(language, "Global warnings", "Advertencias globales", "Avisos globais")}
          </h3>
          <ul className="list-disc space-y-1 pl-5 text-sm text-amber-800 dark:text-amber-200">
            {globalWarnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </section>
      )}

      {!gapFields.some(({ result }) => result?.status === "found") &&
        !warningFields.length &&
        !globalWarnings.length && (
          <p className="text-sm text-slate-500">
            {copy(
              language,
              "No gaps or warnings to display.",
              "No hay brechas ni advertencias para mostrar.",
              "Não há lacunas nem avisos para exibir."
            )}
          </p>
        )}
    </div>
  );
}

export function RfpScopeAnalysisActions({
  onReviewScope,
  reviewing = false,
  onRegenerate,
  regenerating = false,
  blocked = false,
}) {
  const actionCls =
    "rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800";
  const pressedCls =
    "rounded-lg border border-slate-800 bg-slate-800 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60 dark:border-slate-200 dark:bg-slate-200 dark:text-slate-900";
  return (
    <div className="flex flex-wrap items-center justify-end gap-2" data-testid="rfp-scope-analysis-actions">
      {onReviewScope ? (
        <button
          type="button"
          data-testid="rfp-review-scope"
          aria-pressed={reviewing}
          className={reviewing ? pressedCls : actionCls}
          onClick={onReviewScope}
        >
          Review RFP Scope
        </button>
      ) : null}
      {onRegenerate ? (
        <button
          type="button"
          data-testid="rfp-scope-analysis-regenerate"
          disabled={regenerating || blocked}
          className={actionCls}
          onClick={onRegenerate}
        >
          {regenerating ? "Generating…" : "Regenerate Analysis"}
        </button>
      ) : null}
    </div>
  );
}

export default function RfpScopeAnalysisView({
  payload,
  loading,
  error,
  onRegenerate,
  regenerating,
  onEvidenceClick,
  onReviewScope,
  reviewingScope = false,
  showActions = true,
}) {
  const [activeTab, setActiveTab] = useState("overview");
  const status = payload?.status || {};
  const analysis = useMemo(() => assembleScopeAnalysis(payload?.analysis), [payload?.analysis]);
  const language = useMemo(() => analysisLanguage(analysis), [analysis]);
  const tabs = language === "es" ? TABS_ES : language === "pt" ? TABS_PT : TABS;
  const isRunning = status.status === "running";
  const blocked = !isRunning && !status.approved_scope_ready;
  const blockedReason =
    status.blocked_reason ||
    payload?.blocked_reason ||
    "RFP Discovery Scope must be reviewed and approved before this Analysis can run.";

  if ((loading || isRunning) && !analysis) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-8 text-sm dark:border-slate-700 dark:bg-slate-900/40">
        <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" />
        Loading RFP Discovery Scope Analysis…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="rfp-scope-analysis-view">
      {status.stale_scope ? (
        <p
          className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
          data-testid="rfp-scope-analysis-stale"
        >
          RFP Discovery Scope has changed since this Analysis was generated.
        </p>
      ) : null}

      {blocked ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100">
          {blockedReason}
        </div>
      ) : null}

      {error ? (
        <p className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-900 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-100">
          {error}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="max-w-2xl text-sm text-slate-500 dark:text-slate-400">
          Uses the engineer-approved Scope Discovery technologies and pages. Does not rediscover products.
        </p>
        {showActions ? (
          <RfpScopeAnalysisActions
            onReviewScope={onReviewScope}
            reviewing={reviewingScope}
            onRegenerate={onRegenerate}
            regenerating={regenerating}
            blocked={blocked}
          />
        ) : null}
      </div>

      {!analysis && !blocked ? (
        <p className="text-sm text-slate-500">No RFP Discovery Scope Analysis has been generated yet.</p>
      ) : null}

      {analysis ? (
        <>
          <div className="sticky top-0 z-10 -mx-1 overflow-x-auto bg-white/95 px-1 py-2 backdrop-blur dark:bg-slate-900/95">
            <div className="flex min-w-max gap-1 border-b border-slate-200 dark:border-slate-700" role="tablist">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  className={`whitespace-nowrap border-b-2 px-3 py-2 text-sm font-semibold transition-colors ${
                    activeTab === tab.id
                      ? "border-slate-900 text-slate-900 dark:border-white dark:text-white"
                      : "border-transparent font-medium text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {activeTab === "overview" && (
            <OverviewTab analysis={analysis} language={language} onEvidenceClick={onEvidenceClick} />
          )}
          {activeTab === "design" && (
            <DesignTab analysis={analysis} language={language} onEvidenceClick={onEvidenceClick} />
          )}
          {activeTab === "sti" && <StiTab analysis={analysis} language={language} onEvidenceClick={onEvidenceClick} />}
          {activeTab === "commercial" && (
            <CommercialTab analysis={analysis} language={language} onEvidenceClick={onEvidenceClick} />
          )}
          {activeTab === "gaps" && <GapsTab analysis={analysis} language={language} onEvidenceClick={onEvidenceClick} />}
        </>
      ) : null}
    </div>
  );
}
